// Het vlak Configuratie (W6 ronde 6, besluit V1; research §3.7): waarop de machine draait.
//
// Vier vragen, vier tabbladen:
//  · Onderwerpen    — welke onderwerpen kent de machine, hoeveel trends en
//                     vaardigheden noemen ze, en wat heeft er (nog) geen? Plus
//                     voorstellen uit de betekenis (V6: een dry-run, zonder naam
//                     en zonder "live zetten").
//  · Zoekopdrachten — uit welke woorden bouwt de oogst zijn vragen, en wat
//                     leverden die vragen op?
//  · Instellingen   — plafond, dekkingsdoelen en standaardkrediet (V5): de
//                     standaard uit de yaml naast wat nu geldt, en zelf vast te
//                     stellen.
//  · Beroepen       — per sector welke UWV-beroepen erbij horen (bouwrun baan
//                     C2, ownerantwoord 13): de best guess naast de aanpassing,
//                     zelf aan te passen en terug te zetten (ConfiguratieBeroepen).
//
// De eerste drie komen uit één antwoord (GET /api/beheer/configuratie), dus deze
// schil haalt één keer op, bij het openen. Beroepen heeft een eigen antwoord
// (GET /api/beheer/beroepen) en haalt pas op als het tabblad voor het eerst opent. De voorstellen uit de betekenis
// duren seconden en halen alleen op een klik op (ConfiguratieOnderwerpen).
//
// De tabbladen blijven alle staan (Beroepen vanaf de eerste keer openen); alleen het gekozen tabblad is
// zichtbaar (review frontend ronde 6). Wie tijdens een trage opslag of tijdens
// de voorstellen (~5 s) even naar een ander tabblad kijkt, komt terug bij
// hetzelfde: de formulieren nog dicht, de melding die alsnog binnenkomt, de
// getypte tekst en de voorstellen. Werd alleen het gekozen tabblad getekend,
// dan vergat het scherm bij elke wissel dat er iets liep — en kon er een
// tweede opslag beginnen.
//
// De regel die hier het zwaarst weegt: de server is de baas. Hij stuurt de
// tellingen, de zinnen, de woorden bij de sleutels en de controle op een
// instelling; dit vlak rekent geen getal, aandeel of oordeel uit.
//
// Na het opslaan van een instelling vraagt het vlak het hele antwoord opnieuw.
// Elke vraag krijgt een volgnummer, en de schil houdt bij welke vraag het laatst
// terugkwam — gelukt óf mislukt. Zo weet het tabblad Instellingen wanneer de
// ronde ná het opslaan binnen is, ook als de server precies hetzelfde
// terugstuurt of dezelfde foutzin nog eens geeft (zelfde afspraak als de
// steekproef op Kwaliteit; useResource negeert een oudere vraag zodra er een
// nieuwere loopt).

import { useEffect, useRef, useState } from "react";

import { Card } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type { ConfiguratieResponse } from "../../contracts";
import { color, radius } from "../../tokens";
import { datum, noot } from "./BronnenStukken";
import { Beroepen } from "./ConfiguratieBeroepen";
import { Instellingen } from "./ConfiguratieInstellingen";
import { Onderwerpen } from "./ConfiguratieOnderwerpen";
import { Zoekopdrachten } from "./ConfiguratieZoekopdrachten";

/** De ascii-sleutel reist mee naar het gebruikslog ("Beheer/Configuratie/Instellingen");
 *  het label is wat de beheerder leest. */
type TabKey = "Onderwerpen" | "Zoekopdrachten" | "Instellingen" | "Beroepen";

const TABS: readonly { key: TabKey; label: string }[] = [
  { key: "Onderwerpen", label: "Onderwerpen" },
  { key: "Zoekopdrachten", label: "Zoekopdrachten" },
  { key: "Instellingen", label: "Instellingen" },
  { key: "Beroepen", label: "Beroepen" },
];

/** Het antwoord met het volgnummer van de vraag die het ophaalde. */
type Binnen = { data: ConfiguratieResponse; ronde: number };

export function Configuratie() {
  const [tab, setTab] = useState<TabKey>("Onderwerpen");
  // Baan C2: Beroepen haalt pas op bij de eerste keer openen, en blijft daarna
  // staan (net als de andere tabbladen), zodat een lopende opslag niet verdwijnt.
  const [beroepenOpen, setBeroepenOpen] = useState(false);
  useEffect(() => {
    if (tab === "Beroepen") setBeroepenOpen(true);
  }, [tab]);
  const gevraagd = useRef(0);
  // Een mislukte vraag draagt geen data, dus haar volgnummer staat apart. Een
  // eigen stand (geen ref): elke mislukte ronde moet het scherm laten
  // bijwerken, ook als de foutzin dezelfde is als de vorige. Alleen de
  // nieuwste vraag telt: een oudere die useResource al negeert, mag hier niet
  // alsnog "mislukt" melden terwijl de nieuwe nog loopt.
  const [misluktRonde, setMisluktRonde] = useState(0);
  const bron = useResource<Binnen>(() => {
    const ronde = ++gevraagd.current;
    return api.configuratie().then(
      (data) => ({ data, ronde }),
      (err: unknown) => {
        if (ronde === gevraagd.current) setMisluktRonde(ronde);
        throw err;
      },
    );
  }, []);

  // Welk tabblad, zoals de schil het vlak meldt (W6, research §4): alleen WAAR
  // iemand kijkt, nooit wat erin staat. Ook het eerste tabblad telt.
  // Vuur-en-vergeet: een telling die faalt mag het scherm nooit raken.
  useEffect(() => {
    void api.gebruik({ scherm: `Beheer/Configuratie/${tab}` }).catch(() => {});
  }, [tab]);

  // De laatste vraag die terugkwam, gelukt of mislukt (alleen voor de
  // volgorde, nooit op het scherm).
  const dataRonde = bron.data?.ronde ?? 0;
  const binnen = Math.max(dataRonde, misluktRonde);
  // Mislukte die laatste vraag? Uit dezelfde twee volgnummers, niet uit
  // `bron.error`: die zet useResource een stap later, en dan zou het tabblad
  // Instellingen heel even "gelukt" lezen bij een ronde die mislukte.
  const ophaalMislukt = misluktRonde > dataRonde;
  // Opnieuw ophalen; geeft het volgnummer van de laatste vraag vóór deze ronde,
  // zodat de aanroeper weet welke terugkomst bij zijn opslag hoort.
  const herlaad = () => {
    const na = gevraagd.current;
    bron.reload();
    return na;
  };

  const d = bron.data?.data ?? null;

  return (
    <div>
      {/* Hetzelfde gesegmenteerde patroon als de subnavigatie van Beheer. */}
      <nav
        aria-label="Onderdelen van Configuratie"
        style={{
          display: "inline-flex",
          flexWrap: "wrap",
          gap: 4,
          background: color.lineFaint,
          borderRadius: radius.input,
          padding: 3,
          marginBottom: 18,
        }}
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={"view-btn" + (tab === t.key ? " active" : "")}
            aria-pressed={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {!d ? (
        <Card style={{ padding: 18 }}>
          {bron.error ? (
            <LoadError reason={bron.error} onRetry={bron.reload} />
          ) : (
            <EmptyReason reason="de configuratie wordt opgehaald" />
          )}
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Mislukte een herlaadronde (na een opslag), dan blijft de vorige
              stand staan — dimmen zou liegen dat er nog iets komt — maar de
              fout staat erboven, met de knop om het opnieuw te proberen. */}
          {bron.error && (
            <Card style={{ padding: 18 }}>
              <LoadError reason={bron.error} onRetry={bron.reload} />
            </Card>
          )}
          <div style={{ opacity: bron.stale ? 0.6 : 1, transition: "opacity 0.15s" }}>
            {/* Alle drie getekend, één zichtbaar (zie de kop): `hidden` haalt
                een verborgen tabblad ook uit de toetsenbordvolgorde en uit
                wat een schermlezer voorleest. */}
            <div data-paneel="Onderwerpen" hidden={tab !== "Onderwerpen"}>
              <Onderwerpen topics={d.topics} />
            </div>
            <div data-paneel="Zoekopdrachten" hidden={tab !== "Zoekopdrachten"}>
              <Zoekopdrachten z={d.zoekopdrachten} />
            </div>
            <div data-paneel="Instellingen" hidden={tab !== "Instellingen"}>
              <Instellingen
                data={d}
                binnen={binnen}
                ophaalMislukt={ophaalMislukt}
                herlaad={herlaad}
              />
            </div>
            <div data-paneel="Beroepen" hidden={tab !== "Beroepen"}>
              {beroepenOpen && <Beroepen />}
            </div>
          </div>
          <p style={{ ...noot, marginTop: 0 }}>
            {`Gemeten op ${datum(d.gemetenOp) ?? "een onbekend moment"}.`}
          </p>
        </div>
      )}
    </div>
  );
}
